import { supabase } from '../lib/supabase';

/**
 * Service for managing full planner PDF storage in Supabase
 * Handles upload, download, and cleanup of generated planner PDFs
 */

const BUCKET_NAME = 'generated-planners';
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

/**
 * Generate file path following naming convention:
 * user_{userId}/planner{year}_{configId}_{timestamp}.pdf
 */
const generateFilePath = (userId, year, configId) => {
  const timestamp = Date.now();
  return `user_${userId}/planner${year}_${configId}_${timestamp}.pdf`;
};

/**
 * Upload PDF to storage with retry logic
 */
const uploadWithRetry = async (filePath, pdfBlob, retryCount = 0) => {
  try {
    const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.upload(filePath, pdfBlob, {
        contentType: 'application/pdf',
        cacheControl: '3600',
        upsert: false
      });

    if (error) throw error;
    return data;
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      console.warn(`Upload attempt ${retryCount + 1} failed, retrying...`, error);
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
      return uploadWithRetry(filePath, pdfBlob, retryCount + 1);
    }
    throw error;
  }
};

/**
 * Delete old PDFs for same configuration (keeping most recent)
 */
const replaceExistingPdf = async (userId, configId, currentFilePath) => {
  try {
    // Get all full planners for this user and config
    const { data: existingPlanners, error: fetchError } = await supabase
      ?.from('planner_configurations')
      ?.select('generated_pdf_path')
      ?.eq('user_id', userId)
      ?.eq('id', configId)
      ?.eq('is_full_planner', true)
      ?.neq('generated_pdf_path', currentFilePath)
      ?.not('generated_pdf_path', 'is', null);

    if (fetchError) {
      console.error('Error fetching existing planners:', fetchError);
      return;
    }

    // Delete old files from storage
    if (existingPlanners && existingPlanners?.length > 0) {
      const filePaths = existingPlanners?.map(p => p?.generated_pdf_path);
      const { error: deleteError } = await supabase?.storage?.from(BUCKET_NAME)?.remove(filePaths);

      if (deleteError) {
        console.error('Error deleting old files:', deleteError);
      }
    }
  } catch (error) {
    console.error('Error in replaceExistingPdf:', error);
  }
};

export const plannerStorageService = {
  /**
   * Upload full planner PDF and create/update database record
   * @param {Blob} pdfBlob - The generated PDF as a Blob
   * @param {Object} plannerData - Planner configuration data
   * @param {string} configId - Planner configuration ID (optional, creates new if not provided)
   * @returns {Promise<Object>} Object with storageUrl, blobUrl, and configId
   */
  async uploadFullPlanner(pdfBlob, plannerData, configId = null) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Extract year from planner data
      const year = plannerData?.personalization?.year || new Date()?.getFullYear();

      // Create config record first if not provided
      let finalConfigId = configId;
      if (!finalConfigId) {
        const configData = {
          plannerName: plannerData?.personalization?.name || `My Planner ${year}`,
          pageSize: plannerData?.size || 'A4',
          layoutType: plannerData?.plannerType || 'daily',
          colorScheme: plannerData?.theme || 'default',
          configurationData: plannerData,
          isFavorite: false,
          isFullPlanner: true
        };

        const { data: newConfig, error: configError } = await supabase
          ?.from('planner_configurations')
          ?.insert({
            user_id: user?.id,
            planner_name: configData?.plannerName,
            page_size: configData?.pageSize,
            layout_type: configData?.layoutType,
            color_scheme: configData?.colorScheme,
            configuration_data: configData?.configurationData,
            is_favorite: configData?.isFavorite,
            is_full_planner: true
          })
          ?.select();

        if (configError) throw configError;
        
        // Extract first record from array instead of using .single()
        if (!newConfig || newConfig?.length === 0) {
          throw new Error('Failed to create planner configuration record');
        }
        finalConfigId = newConfig?.[0]?.id;
      }

      // Generate file path
      const filePath = generateFilePath(user?.id, year, finalConfigId);

      // Create blob URL for immediate download (fallback)
      const blobUrl = URL.createObjectURL(pdfBlob);

      let storageUrl = null;
      let uploadError = null;

      try {
        // Attempt upload to storage with retry
        await uploadWithRetry(filePath, pdfBlob);

        // Get signed URL for private bucket (24 hour expiry)
        const { data: urlData, error: urlError } = await supabase?.storage?.from(BUCKET_NAME)?.createSignedUrl(filePath, 86400); // 24 hours

        if (urlError) throw urlError;
        storageUrl = urlData?.signedUrl;

        // Update database record with storage path
        const { error: updateError } = await supabase?.from('planner_configurations')?.update({
            generated_pdf_path: filePath,
            is_full_planner: true
          })?.eq('id', finalConfigId)?.eq('user_id', user?.id);

        if (updateError) throw updateError;

        // Replace old PDFs for same configuration
        await replaceExistingPdf(user?.id, finalConfigId, filePath);

      } catch (error) {
        uploadError = error;
        console.error('Storage upload failed, using blob URL fallback:', error);
      }

      return {
        success: !uploadError,
        storageUrl,
        blobUrl,
        configId: finalConfigId,
        uploadError: uploadError ? uploadError?.message : null
      };

    } catch (error) {
      console.error('Error in uploadFullPlanner:', error);
      throw error;
    }
  },

  /**
   * Get signed URL for stored PDF
   * @param {string} filePath - Path to file in storage
   * @param {number} expirySeconds - URL expiry time in seconds (default 24 hours)
   * @returns {Promise<string>} Signed URL
   */
  async getSignedUrl(filePath, expirySeconds = 86400) {
    try {
      const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.createSignedUrl(filePath, expirySeconds);

      if (error) throw error;
      return data?.signedUrl;
    } catch (error) {
      console.error('Error getting signed URL:', error);
      throw error;
    }
  },

  /**
   * Download PDF from storage
   * @param {string} filePath - Path to file in storage
   * @returns {Promise<Blob>} PDF Blob
   */
  async downloadPdf(filePath) {
    try {
      const { data, error } = await supabase?.storage?.from(BUCKET_NAME)?.download(filePath);

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error downloading PDF:', error);
      throw error;
    }
  },

  /**
   * Delete PDF from storage
   * @param {string} filePath - Path to file in storage
   * @returns {Promise<boolean>}
   */
  async deletePdf(filePath) {
    try {
      const { error } = await supabase?.storage?.from(BUCKET_NAME)?.remove([filePath]);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting PDF:', error);
      throw error;
    }
  },

  /**
   * Trigger manual cleanup of old planners
   * @returns {Promise<void>}
   */
  async cleanupOldPlanners() {
    try {
      const { error } = await supabase?.rpc('cleanup_old_full_planners');
      if (error) throw error;
    } catch (error) {
      console.error('Error cleaning up old planners:', error);
      throw error;
    }
  }
};